import { useState, useCallback, useEffect } from 'react';
import useDebounceEffect from '../../../hooks/useDebounceEffect';
import { useResizeObserver } from '../../../hooks/useResizeObserver';
import { useIsMobileNavigation } from './useIsMobileNavigation';
export function useHighlightSwoosh(elementRef, debounceDelay) {
    const isMobile = useIsMobileNavigation();
    /**
     * Position of the highlight swoooosh.
     * In this case the left position is the absolute position on the screen
     */
    const [highlightPosition, setHighlightPosition] = useState({
        left: 0,
        width: 0,
    });
    /**
     * Observed width of navigation (or given element)
     */
    const [navigationWidth, setNavigationWidth] = useState(0);
    /**
     * Navigation items with all registered elements and state.
     * In this case it is the active state only
     */
    const [navigationItems, setNavigationItems] = useState([]);
    /**
     * Register function for navigation items.
     * Should be called only once on mount of a item.
     */
    const registerNavigationItem = useCallback(element => {
        setNavigationItems(items => {
            return [
                ...items,
                {
                    element,
                    isActive: false,
                },
            ];
        });
    }, []);
    /**
     * Unregister function for navigation items on unmount
     */
    const unregisterNavigationItem = useCallback(element => {
        setNavigationItems(items => items.filter(current => !Object.is(element, current.element)));
    }, []);
    /**
     * Update the navigation item
     * This sets the active state of each icon
     */
    const updateActiveNavigationItem = useCallback((element, isActive) => {
        setNavigationItems(items => items.map(item => {
            if (Object.is(element, item.element) && isActive !== undefined) {
                return {
                    element,
                    isActive,
                };
            }
            return item;
        }));
    }, []);
    /**
     *  Calculate the Swoosh
     */
    const calculateSwoosh = useCallback(() => {
        const activeItem = navigationItems.find(item => item.isActive);
        if (!activeItem || !elementRef.current) {
            // Reset position if there is no active element / swoosh to the left
            setHighlightPosition({ left: 0, width: 0 });
        }
        else {
            // Set new position and perform the expensive "getBoundingClientRect()" calls
            const rect = activeItem.element.getBoundingClientRect();
            const navigationRect = elementRef.current.getBoundingClientRect();
            setHighlightPosition({
                left: rect.left - navigationRect.left,
                width: rect.width,
            });
        }
    }, [elementRef, navigationItems]);
    /**
     * Observe navigation's width.
     * This is more reliable since the viewport width doesn't
     * include the scrollbar if there is any
     */
    useResizeObserver(([entry]) => {
        setNavigationWidth(entry.contentRect.width);
    }, elementRef);
    /**
     * Effects that set the highlight swoosh if
     * navigation width changes (debounced)
     */
    useDebounceEffect(() => {
        if (!isMobile) {
            calculateSwoosh();
        }
    }, 
    // warning: eslint(react-hooks/exhaustive-deps) does not work here correctly
    [navigationWidth, isMobile], debounceDelay);
    /**
     * Effects that set the highlight swoosh if
     * active navigation item changes / is initially set
     */
    useEffect(() => {
        if (navigationItems && !isMobile) {
            calculateSwoosh();
        }
    }, [navigationItems, calculateSwoosh, isMobile]);
    return {
        registerNavigationItem,
        unregisterNavigationItem,
        updateActiveNavigationItem,
        highlightPosition,
        navigationWidth,
    };
}
